import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api } from '../../lib/apiClient';
import { Button } from '../../components/ui/Button';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { useCurrentOrg } from '../orgs/useCurrentOrg';

type MCPServer = {
  id: string;
  name: string;
  url: string;
  has_auth_header: boolean;
  discovered_tools: { name: string; description?: string; auto_executes?: boolean }[];
  last_probed_at?: string;
  created_at: string;
};

async function listMCP(orgId: string): Promise<MCPServer[]> {
  const res = await api.get<{ data: { items: MCPServer[] } }>(`/v1/orgs/${orgId}/mcp-servers`);
  return res.data.data?.items ?? [];
}

async function registerMCP(orgId: string, input: { name: string; url: string; auth_header?: string }) {
  const res = await api.post<{ data: MCPServer }>(`/v1/orgs/${orgId}/mcp-servers`, input);
  return res.data.data;
}

async function deleteMCP(orgId: string, id: string) {
  await api.delete(`/v1/orgs/${orgId}/mcp-servers/${id}`);
}

export function MCPServersPage() {
  const { org } = useCurrentOrg();
  const qc = useQueryClient();
  const orgId = org?.id ?? '';

  const servers = useQuery({
    queryKey: ['mcp-servers', orgId],
    queryFn: () => listMCP(orgId),
    enabled: !!orgId,
  });

  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [authHeader, setAuthHeader] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => registerMCP(orgId, { name, url, auth_header: authHeader || undefined }),
    onSuccess: () => {
      setName('');
      setUrl('');
      setAuthHeader('');
      qc.invalidateQueries({ queryKey: ['mcp-servers', orgId] });
    },
    onError: (e: unknown) => setError(extractError(e)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteMCP(orgId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mcp-servers', orgId] }),
  });

  function handleCreate(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name || !url) {
      setError('name and url are required');
      return;
    }
    create.mutate();
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tightish text-ink-primary">MCP servers</h1>
        <p className="mt-1 text-sm text-ink-secondary">
          External Model Context Protocol servers your agents can use. Sigil discovers the tools
          each server offers and exposes them as additional options to the LLM. Servers that
          auto-sign or move funds without returning calldata are rejected — Sigil agents only
          consume calldata-returning tools (wallet stays in our TEE).
        </p>
      </div>

      <Card>
        <CardHeader title="Register a server" />
        <CardBody>
          <form onSubmit={handleCreate} className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Name"
              placeholder="1inch"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <Input
              label="URL (SSE / HTTP)"
              placeholder="https://mcp.1inch.io/v1/sse"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              required
            />
            <div className="sm:col-span-2">
              <Input
                label="Auth header (optional, e.g. 'Bearer xyz')"
                type="password"
                value={authHeader}
                onChange={(e) => setAuthHeader(e.target.value)}
              />
            </div>
            {error && (
              <div className="sm:col-span-2 rounded-md border border-danger/20 bg-danger/5 px-3 py-2 text-sm text-danger">
                {error}
              </div>
            )}
            <div className="sm:col-span-2 flex justify-end">
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? 'Registering…' : 'Register server'}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Registered servers" description="Tool discovery runs the first time an agent uses each server." />
        <CardBody className="p-0">
          {servers.data && servers.data.length === 0 && (
            <div className="p-6 text-sm text-ink-tertiary">No servers registered.</div>
          )}
          {servers.data?.map((s) => (
            <div key={s.id} className="border-t border-line-soft p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-ink-primary">{s.name}</p>
                  <p className="mt-0.5 font-mono text-xs text-ink-tertiary">{s.url}</p>
                  {s.has_auth_header && (
                    <p className="mt-1 text-xs text-ink-tertiary">🔒 with auth header</p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  onClick={() => {
                    if (confirm(`Remove ${s.name}? Agents using it will lose its tools.`)) {
                      remove.mutate(s.id);
                    }
                  }}
                >
                  Remove
                </Button>
              </div>
              {s.discovered_tools.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {s.discovered_tools.map((t) => (
                    <span
                      key={t.name}
                      className={`rounded-full border px-2 py-0.5 font-mono text-[10px] ${
                        t.auto_executes
                          ? 'border-danger/30 text-danger'
                          : 'border-line text-ink-secondary'
                      }`}
                    >
                      {t.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </CardBody>
      </Card>
    </div>
  );
}

function extractError(e: unknown): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const resp = (e as { response?: { data?: { error?: { message?: string; code?: string } } } }).response;
    if (resp?.data?.error) return resp.data.error.message ?? resp.data.error.code ?? 'unknown error';
  }
  return e instanceof Error ? e.message : 'unknown error';
}
